// src/pages/finance-portal/agent-console/AgentConsole.js
// AI Agentic Orchestration Core — Agent Console UI covering every phase:
//   Runs, Tools, Agents, Workflows, Memory, Escalations, Health, Cost

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Alert, CircularProgress, Divider, Stack, IconButton, Tooltip,
    Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BuildIcon from '@mui/icons-material/Build';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#6366f1';

const STATUS_COLORS = {
    PENDING:         { bg: '#e0e7ff', color: '#3730a3' },
    RUNNING:         { bg: '#fef9c3', color: '#854d0e' },
    DONE:            { bg: '#dcfce7', color: '#166534' },
    FAILED:          { bg: '#fee2e2', color: '#991b1b' },
    NEEDS_APPROVAL:  { bg: '#fef3c7', color: '#92400e' },
    READY:           { bg: '#dcfce7', color: '#166534' },
    EXECUTING:       { bg: '#fef9c3', color: '#854d0e' },
    PAUSED:          { bg: '#e5e7eb', color: '#374151' },
    TERMINATED:      { bg: '#f1f5f9', color: '#64748b' },
    CRASHED:         { bg: '#fee2e2', color: '#991b1b' },
    OPEN:            { bg: '#fef3c7', color: '#92400e' },
    RESOLVED:        { bg: '#dcfce7', color: '#166534' },
    OVERRIDDEN:      { bg: '#ede9fe', color: '#5b21b6' },
    TIMED_OUT:       { bg: '#fee2e2', color: '#991b1b' },
};

const StatusChip = ({ status }) => {
    const c = STATUS_COLORS[status] || { bg: '#f1f5f9', color: '#475569' };
    return (
        <Chip
            label={status}
            size="small"
            sx={{ bgcolor: c.bg, color: c.color, fontWeight: 600, fontFamily }}
        />
    );
};

const SUGGESTED_PROMPTS = [
    'What is my total unpaid AR right now?',
    'Break down my accounts receivable by aging bucket.',
    'How many invoices do I have in each status?',
    'Show me the 5 most recent Stripe transactions.',
    'How many bills are currently unpaid?',
];

export default function AgentConsole() {
    const [tab, setTab] = useState(0);

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SmartToyIcon sx={{ color: ACCENT, fontSize: 32 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
                    Finance Agent Console
                </Typography>
                <Chip label="AI Orchestration Core" size="small"
                      sx={{ bgcolor: '#eef2ff', color: ACCENT, fontWeight: 600, fontFamily }} />
            </Stack>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab label="Runs" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Tools" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Agents" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Workflows" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Memory" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Escalations" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Health" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
                <Tab label="Cost" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }} />
            </Tabs>

            {tab === 0 && <RunsTab />}
            {tab === 1 && <ToolsTab />}
            {tab === 2 && <AgentsTab />}
            {tab === 3 && <WorkflowsTab />}
            {tab === 4 && <MemoryTab />}
            {tab === 5 && <EscalationsTab />}
            {tab === 6 && <HealthTab />}
            {tab === 7 && <CostTab />}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Runs tab (the original Agent Console)
// ---------------------------------------------------------------------------

function RunsTab() {
    const [task, setTask] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const [latestRun, setLatestRun] = useState(null);
    const [runs, setRuns] = useState([]);
    const [error, setError] = useState(null);
    const [detailRun, setDetailRun] = useState(null);

    const loadRuns = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/agent/runs', { params: { per_page: 20 } });
            setRuns(res.data?.data || []);
        } catch (e) {
            setError('Failed to load runs: ' + (e.response?.data?.message || e.message));
        }
    }, []);

    useEffect(() => { loadRuns(); }, [loadRuns]);

    const handleRun = async () => {
        const trimmed = task.trim();
        if (!trimmed) return;
        setIsRunning(true);
        setError(null);
        setLatestRun(null);
        try {
            const res = await axiosInstance.post('/api/finance/agent/run', { task: trimmed });
            setLatestRun(res.data?.run || null);
            setTask('');
            await loadRuns();
        } catch (e) {
            setError('Agent run failed: ' + (e.response?.data?.message || e.message));
        } finally {
            setIsRunning(false);
        }
    };

    const openDetail = async (run) => {
        setDetailRun(run);
        try {
            const res = await axiosInstance.get(`/api/finance/agent/runs/${run.id}`);
            setDetailRun(res.data?.run || run);
        } catch (_) { /* keep summary */ }
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Ask the agent
                </Typography>
                <TextField
                    fullWidth multiline minRows={3}
                    placeholder="e.g. What is my total unpaid AR right now?"
                    value={task}
                    onChange={(e) => setTask(e.target.value)}
                    disabled={isRunning}
                    sx={{ fontFamily }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                    {SUGGESTED_PROMPTS.map((p) => (
                        <Chip key={p} label={p} size="small" clickable onClick={() => setTask(p)}
                              sx={{ mb: 0.5, fontFamily }} />
                    ))}
                </Stack>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleRun}
                        disabled={isRunning || !task.trim()}
                        startIcon={isRunning ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4f46e5' }, fontFamily }}
                    >
                        {isRunning ? 'Running…' : 'Run'}
                    </Button>
                </Box>
            </Paper>

            {latestRun && (
                <Card sx={{ mb: 3, borderLeft: `4px solid ${ACCENT}` }}>
                    <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <StatusChip status={latestRun.status} />
                            {latestRun.confidence != null && (
                                <Chip size="small" label={`confidence ${(latestRun.confidence * 100).toFixed(0)}%`}
                                      sx={{ fontFamily }} />
                            )}
                            <Chip size="small" label={`${latestRun.iterations} iterations`} sx={{ fontFamily }} />
                            {latestRun.usd_cost != null && (
                                <Chip size="small"
                                      label={`$${Number(latestRun.usd_cost).toFixed(6)}`}
                                      sx={{ fontFamily }} />
                            )}
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#64748b', fontFamily }}>TASK</Typography>
                        <Typography variant="body2" sx={{ mb: 2, fontFamily }}>{latestRun.task}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ color: '#64748b', fontFamily }}>ANSWER</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily }}>
                            {latestRun.result_text || latestRun.error || '(no answer)'}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily }}>Recent runs</Typography>
                    <Tooltip title="Refresh"><IconButton size="small" onClick={loadRuns}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
                <DataTable
                    columns={['Task', 'Status', 'Conf.', 'Iter.', 'When', '']}
                    rows={runs.map(r => ({
                        id: r.id,
                        cells: [
                            <Box sx={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.task}</Box>,
                            <StatusChip status={r.status} />,
                            r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : '—',
                            r.iterations,
                            r.created_at ? new Date(r.created_at).toLocaleString() : '—',
                            <IconButton size="small" onClick={() => openDetail(r)}><VisibilityIcon fontSize="small" /></IconButton>,
                        ],
                    }))}
                />
            </Paper>

            <Dialog open={!!detailRun} onClose={() => setDetailRun(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Run #{detailRun?.id}</DialogTitle>
                <DialogContent dividers>
                    {detailRun && (
                        <Box>
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <StatusChip status={detailRun.status} />
                                {detailRun.confidence != null && (
                                    <Chip size="small" label={`confidence ${(detailRun.confidence * 100).toFixed(0)}%`} sx={{ fontFamily }} />
                                )}
                                {detailRun.trace_id && (
                                    <Chip size="small" label={`trace ${detailRun.trace_id.slice(0,8)}`} sx={{ fontFamily }} />
                                )}
                            </Stack>
                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily }}>TASK</Typography>
                            <Typography variant="body2" sx={{ mb: 2, fontFamily }}>{detailRun.task}</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontFamily }}>ANSWER</Typography>
                            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap', fontFamily }}>
                                {detailRun.result_text || detailRun.error || '(no answer)'}
                            </Typography>
                            {Array.isArray(detailRun.tool_calls) && detailRun.tool_calls.length > 0 && (
                                <>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontFamily }}>TOOL CALLS</Typography>
                                    {detailRun.tool_calls.map((tc, idx) => (
                                        <Box key={idx} sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, fontFamily }}>{tc.tool}</Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, monospace', display: 'block', whiteSpace: 'pre-wrap' }}>
                                                args: {JSON.stringify(tc.args)}
                                            </Typography>
                                            <Typography variant="caption" sx={{
                                                fontFamily: 'ui-monospace, monospace', display: 'block',
                                                whiteSpace: 'pre-wrap', color: tc.result?.success ? '#166534' : '#991b1b'
                                            }}>
                                                result: {JSON.stringify(tc.result?.value ?? tc.result?.error)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailRun(null)} sx={{ fontFamily }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Generic table helper
// ---------------------------------------------------------------------------

function DataTable({ columns, rows, emptyMessage = 'No data' }) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {columns.map((c, i) => (
                            <TableCell key={i} sx={{ fontFamily, fontWeight: 700 }}>{c}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length} align="center" sx={{ color: '#94a3b8', fontFamily }}>
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((r) => (
                        <TableRow key={r.id} hover>
                            {r.cells.map((cell, i) => (
                                <TableCell key={i} sx={{ fontFamily }}>{cell}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ---------------------------------------------------------------------------
// Tools tab
// ---------------------------------------------------------------------------

function ToolsTab() {
    const [tools, setTools] = useState([]);
    const [invocations, setInvocations] = useState([]);

    const load = async () => {
        try {
            const [t, inv] = await Promise.all([
                axiosInstance.get('/api/finance/agent/tools'),
                axiosInstance.get('/api/finance/agent/tool-invocations', { params: { per_page: 50 } }),
            ]);
            setTools(t.data?.tools || []);
            setInvocations(inv.data?.data || []);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
            <Paper sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <BuildIcon sx={{ fontSize: 18, color: ACCENT }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily }}>
                        Registered tools ({tools.length})
                    </Typography>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                {tools.map((t) => (
                    <Box key={t.tool_key} sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily }}>{t.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontFamily, display: 'block' }}>
                            {t.tool_key} {t.circuit_open && <Chip size="small" label="CIRCUIT OPEN" sx={{ ml: 1, bgcolor: '#fee2e2', color: '#991b1b' }} />}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', fontFamily }}>{t.description}</Typography>
                    </Box>
                ))}
            </Paper>
            <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Recent invocations
                </Typography>
                <DataTable
                    columns={['Tool', 'OK', 'Latency', 'When']}
                    rows={invocations.map(i => ({
                        id: i.id,
                        cells: [
                            i.tool_key,
                            i.success ? '✓' : <Chip size="small" label={(i.error || '').slice(0, 40)} sx={{ bgcolor: '#fee2e2', color: '#991b1b' }} />,
                            i.latency_ms != null ? `${i.latency_ms}ms` : '—',
                            i.created_at ? new Date(i.created_at).toLocaleTimeString() : '—',
                        ],
                    }))}
                />
            </Paper>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Agents tab — definitions + instances
// ---------------------------------------------------------------------------

function AgentsTab() {
    const [definitions, setDefinitions] = useState([]);
    const [instances, setInstances] = useState([]);
    const [form, setForm] = useState('{\n  "name": "ar-summary-agent",\n  "role": "analyst",\n  "objective": "Answer AR questions",\n  "tools": ["finance.ar_aging_summary", "finance.unpaid_invoices_total"]\n}');
    const [message, setMessage] = useState(null);

    const load = async () => {
        try {
            const [d, i] = await Promise.all([
                axiosInstance.get('/api/finance/agent/definitions'),
                axiosInstance.get('/api/finance/agent/instances'),
            ]);
            setDefinitions(d.data?.data || []);
            setInstances(i.data?.data || []);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    const saveDefinition = async () => {
        setMessage(null);
        try {
            const body = JSON.parse(form);
            await axiosInstance.post('/api/finance/agent/definitions', body);
            await load();
            setMessage({ severity: 'success', text: 'Definition saved.' });
        } catch (e) {
            setMessage({ severity: 'error', text: 'Save failed: ' + (e.response?.data?.message || e.message) });
        }
    };

    const startInstance = async (defId) => {
        try {
            await axiosInstance.post('/api/finance/agent/instances/start', { agent_definition_id: defId });
            await load();
        } catch (_) {}
    };

    const instanceAction = async (instId, action) => {
        try {
            await axiosInstance.post(`/api/finance/agent/instances/${instId}/${action}`);
            await load();
        } catch (_) {}
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
            <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Agent definitions ({definitions.length})
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {definitions.map(d => (
                    <Box key={d.id} sx={{ mb: 1.5, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily }}>{d.name} v{d.version}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontFamily, display: 'block' }}>{d.role} · {d.model}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontFamily }}>
                                    tools: {(d.allowed_tool_keys || []).join(', ') || '—'}
                                </Typography>
                            </Box>
                            <Button size="small" variant="outlined" onClick={() => startInstance(d.id)} sx={{ fontFamily }}>
                                Start
                            </Button>
                        </Stack>
                    </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Upsert definition (DSL JSON)
                </Typography>
                <TextField fullWidth multiline minRows={8} value={form}
                            onChange={(e) => setForm(e.target.value)}
                            inputProps={{ style: { fontFamily: 'ui-monospace, monospace', fontSize: 12 } }} />
                <Box sx={{ mt: 1 }}>
                    <Button variant="contained" onClick={saveDefinition}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4f46e5' }, fontFamily }}>
                        Save
                    </Button>
                </Box>
                {message && <Alert severity={message.severity} sx={{ mt: 1 }}>{message.text}</Alert>}
            </Paper>

            <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Agent instances ({instances.length})
                </Typography>
                <DataTable
                    columns={['ID', 'Definition', 'Status', 'Restarts', 'Actions']}
                    rows={instances.map(i => ({
                        id: i.id,
                        cells: [
                            i.id,
                            i.agent_definition_id,
                            <StatusChip status={i.status} />,
                            i.restart_count,
                            <Stack direction="row" spacing={0.5}>
                                {i.status === 'PAUSED'
                                    ? <Button size="small" onClick={() => instanceAction(i.id, 'resume')} sx={{ fontFamily }}>Resume</Button>
                                    : <Button size="small" onClick={() => instanceAction(i.id, 'pause')} sx={{ fontFamily }}>Pause</Button>}
                                <Button size="small" color="error" onClick={() => instanceAction(i.id, 'terminate')} sx={{ fontFamily }}>
                                    Terminate
                                </Button>
                            </Stack>,
                        ],
                    }))}
                />
            </Paper>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Workflows tab
// ---------------------------------------------------------------------------

function WorkflowsTab() {
    const [workflows, setWorkflows] = useState([]);
    const [form, setForm] = useState('{\n  "name": "sequential-ar-health",\n  "dag": {\n    "pattern": "sequential",\n    "nodes": [\n      { "id": "a", "task": "Return the total unpaid AR right now as a single number." },\n      { "id": "b", "task": "Given AR total ${a.answer}, is that healthy for a typical small business? One sentence." }\n    ]\n  }\n}');

    const load = async () => {
        try {
            const res = await axiosInstance.get('/api/finance/agent/workflows');
            setWorkflows(res.data?.data || []);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            const body = JSON.parse(form);
            await axiosInstance.post('/api/finance/agent/workflows', body);
            await load();
        } catch (e) {
            alert('Parse/save error: ' + (e.response?.data?.message || e.message));
        }
    };

    const run = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/agent/workflows/${id}/run`);
            await load();
        } catch (_) {}
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
            <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Create workflow (JSON)
                </Typography>
                <TextField fullWidth multiline minRows={10} value={form}
                           onChange={(e) => setForm(e.target.value)}
                           inputProps={{ style: { fontFamily: 'ui-monospace, monospace', fontSize: 12 } }} />
                <Box sx={{ mt: 1 }}>
                    <Button variant="contained" onClick={create}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#4f46e5' }, fontFamily }}>
                        Create
                    </Button>
                </Box>
            </Paper>
            <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Workflows ({workflows.length})
                </Typography>
                <DataTable
                    columns={['Name', 'Status', 'Trace', 'Run']}
                    rows={workflows.map(w => ({
                        id: w.id,
                        cells: [
                            w.name,
                            <StatusChip status={w.status} />,
                            w.trace_id ? w.trace_id.slice(0, 8) : '—',
                            <Button size="small" variant="outlined" onClick={() => run(w.id)} sx={{ fontFamily }}>Run</Button>,
                        ],
                    }))}
                />
            </Paper>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Memory tab — episodic + KB + preferences + shared state
// ---------------------------------------------------------------------------

function MemoryTab() {
    const [episodic, setEpisodic] = useState([]);
    const [kb, setKb] = useState([]);
    const [prefs, setPrefs] = useState([]);
    const [shared, setShared] = useState([]);

    const load = async () => {
        try {
            const [e, k, p, s] = await Promise.all([
                axiosInstance.get('/api/finance/agent/episodic'),
                axiosInstance.get('/api/finance/agent/kb'),
                axiosInstance.get('/api/finance/agent/preferences'),
                axiosInstance.get('/api/finance/agent/shared-state'),
            ]);
            setEpisodic(e.data?.data || []);
            setKb(k.data?.data || []);
            setPrefs(p.data?.data || []);
            setShared(s.data?.data || []);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
            <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Episodic memory (last 100)
                </Typography>
                <DataTable
                    columns={['Event', 'Summary', 'Importance', 'When']}
                    rows={episodic.map(e => ({
                        id: e.id,
                        cells: [
                            e.event_type,
                            <Box sx={{ maxWidth: 360 }}>{e.summary}</Box>,
                            e.importance,
                            e.created_at ? new Date(e.created_at).toLocaleString() : '—',
                        ],
                    }))}
                />
            </Paper>
            <Paper sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Knowledge base
                </Typography>
                <DataTable
                    columns={['Category', 'Key', 'Value']}
                    rows={kb.map(k => ({
                        id: k.id,
                        cells: [k.category, k.key, <Box sx={{ maxWidth: 300 }}>{k.value}</Box>],
                    }))}
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    User preferences
                </Typography>
                <DataTable
                    columns={['Key', 'Value']}
                    rows={prefs.map(p => ({ id: p.id, cells: [p.key, p.value] }))}
                />

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                    Shared state
                </Typography>
                <DataTable
                    columns={['Namespace', 'Key', 'Version']}
                    rows={shared.map(s => ({ id: s.id, cells: [s.namespace, s.key, s.version] }))}
                />
            </Paper>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Escalations tab
// ---------------------------------------------------------------------------

function EscalationsTab() {
    const [rows, setRows] = useState([]);

    const load = async () => {
        try {
            const res = await axiosInstance.get('/api/finance/agent/escalations');
            setRows(res.data?.data || []);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    const resolve = async (id, decision) => {
        try {
            await axiosInstance.post(`/api/finance/agent/escalations/${id}/resolve`, { decision });
            await load();
        } catch (_) {}
    };

    return (
        <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                Open escalations
            </Typography>
            <DataTable
                columns={['ID', 'Level', 'Reason', 'Status', 'Confidence', 'Actions']}
                rows={rows.map(r => ({
                    id: r.id,
                    cells: [
                        r.id,
                        r.level,
                        <Box sx={{ maxWidth: 360 }}>{r.reason}</Box>,
                        <StatusChip status={r.status} />,
                        r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : '—',
                        r.status === 'OPEN' ? (
                            <Stack direction="row" spacing={0.5}>
                                <Button size="small" color="success" onClick={() => resolve(r.id, 'approve')} sx={{ fontFamily }}>Approve</Button>
                                <Button size="small" color="error" onClick={() => resolve(r.id, 'reject')} sx={{ fontFamily }}>Reject</Button>
                            </Stack>
                        ) : '—',
                    ],
                }))}
            />
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Health tab
// ---------------------------------------------------------------------------

function HealthTab() {
    const [health, setHealth] = useState(null);

    const load = async () => {
        try {
            const res = await axiosInstance.get('/api/finance/agent/health');
            setHealth(res.data?.health || null);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    if (!health) return <CircularProgress size={20} />;

    return (
        <Paper sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip label={`Active: ${health.active_instances}`} sx={{ fontFamily }} />
                <Chip label={`Crashed: ${health.crashed_instances}`} sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontFamily }} />
            </Stack>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                Recent heartbeats
            </Typography>
            <DataTable
                columns={['Instance', 'Status', 'Queue', 'Latency', 'Error rate', 'When']}
                rows={(health.recent_heartbeats || []).map(h => ({
                    id: `${h.agent_instance_id}-${h.recorded_at}`,
                    cells: [
                        h.agent_instance_id, h.status, h.queue_depth,
                        `${h.avg_latency_ms}ms`,
                        `${(h.error_rate * 100).toFixed(1)}%`,
                        h.recorded_at ? new Date(h.recorded_at).toLocaleTimeString() : '—',
                    ],
                }))}
            />
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// Cost tab
// ---------------------------------------------------------------------------

function CostTab() {
    const [status, setStatus] = useState(null);
    const [ledger, setLedger] = useState([]);

    const load = async () => {
        try {
            const [s, l] = await Promise.all([
                axiosInstance.get('/api/finance/agent/cost/status'),
                axiosInstance.get('/api/finance/agent/cost/ledger'),
            ]);
            setStatus(s.data?.budget || null);
            setLedger(l.data?.data || []);
        } catch (_) {}
    };
    useEffect(() => { load(); }, []);

    return (
        <Paper sx={{ p: 2.5 }}>
            {status && (
                <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                    <Chip label={`State: ${status.state}`} sx={{ fontFamily }} />
                    <Chip label={`Daily: ${status.daily.total_tokens.toLocaleString()} / ${status.daily.limit.toLocaleString()}`} sx={{ fontFamily }} />
                    <Chip label={`Monthly: ${status.monthly.total_tokens.toLocaleString()} / ${status.monthly.limit.toLocaleString()}`} sx={{ fontFamily }} />
                    <Chip label={`USD (30d): $${Number(status.monthly.usd_cost).toFixed(4)}`} sx={{ fontFamily }} />
                </Stack>
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontFamily }}>
                Recent ledger rows
            </Typography>
            <DataTable
                columns={['Run', 'Model', 'In', 'Out', 'USD', 'When']}
                rows={ledger.map(l => ({
                    id: l.id,
                    cells: [
                        l.run_id,
                        l.model,
                        l.input_tokens,
                        l.output_tokens,
                        `$${Number(l.usd_cost).toFixed(6)}`,
                        l.created_at ? new Date(l.created_at).toLocaleString() : '—',
                    ],
                }))}
            />
        </Paper>
    );
}
